// ResourceEditDrawer
//
// Drawer lateral 480px para editar un recurso de Formacion (video o PDF).
// Permite cambiar titulo, descripcion, destinatarios, URL (video),
// reemplazar el fichero PDF y reemplazar la imagen.
// El controller del backend borra el blob anterior si se envia uno nuevo,
// asi que solo se anaden al payload los campos modificados.
//
// Componente compartido por REVI y K12 (las audiencias llegan por props).

import React, { useEffect, useState } from "react";
import {
  Drawer,
  Form,
  Input,
  Upload,
  Button,
  Space,
  Popconfirm,
  Alert,
  Checkbox,
  Tag,
  message,
} from "antd";
import {
  PlayCircleOutlined,
  FilePdfOutlined,
  PictureOutlined,
  DeleteOutlined,
  InboxOutlined,
} from "@ant-design/icons";

const MAX_PDF_MB = 25;
const MAX_IMAGE_MB = 5;
const ALLOWED_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
];
const { Dragger } = Upload;

export function ResourceEditDrawer({
  open,
  resource,
  accessToken,
  api,
  onClose,
  onSaved,
  onDeleted,
  audienceOptions = [],
  audienceLabel = "¿Quién debe verlo?",
}) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);

  const [newImage, setNewImage] = useState(null);
  const [newImagePreview, setNewImagePreview] = useState(null);
  const [newDocument, setNewDocument] = useState(null);

  useEffect(() => {
    if (open && resource) {
      form.setFieldsValue({
        title: resource.title,
        description: resource.description,
        url: resource.url,
        audiences: resource.audiences?.length
          ? resource.audiences
          : audienceOptions.slice(0, 1).map((o) => o.value),
      });
      setNewImage(null);
      setNewImagePreview(null);
      setNewDocument(null);
      setError(null);
    }
    // audienceOptions es estable por render de la pagina; no entra en deps
    // para no re-sincronizar el form en cada render del padre.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, resource, form]);

  if (!resource) return null;

  const handlePickImage = (picked) => {
    if (!ALLOWED_IMAGE_TYPES.includes(picked.type)) {
      message.error("Imagen no permitida");
      return false;
    }
    if (picked.size > MAX_IMAGE_MB * 1024 * 1024) {
      message.error(`Supera ${MAX_IMAGE_MB} MB`);
      return false;
    }
    setNewImage(picked);
    const reader = new FileReader();
    reader.onload = (e) => setNewImagePreview(e.target.result);
    reader.readAsDataURL(picked);
    return false;
  };

  const handlePickDocument = (picked) => {
    if (picked.type !== "application/pdf") {
      message.error("Solo PDF");
      return false;
    }
    if (picked.size > MAX_PDF_MB * 1024 * 1024) {
      message.error(`Supera ${MAX_PDF_MB} MB`);
      return false;
    }
    setNewDocument(picked);
    return false;
  };

  const submit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      setError(null);
      const payload = {
        title: values.title?.trim(),
        description: values.description?.trim(),
      };
      if (audienceOptions.length) {
        payload.audiences = (values.audiences || []).join(",");
      }
      if (resource.type === "video") payload.url = values.url?.trim();
      if (newImage) payload.image = newImage;
      if (resource.type === "pdf" && newDocument)
        payload.document = newDocument;

      const updated = await api.update(accessToken, resource._id, payload);
      message.success("Recurso actualizado");
      onSaved?.(updated.resource || updated.data);
      onClose();
    } catch (e) {
      if (e?.errorFields) return;
      setError(e?.body?.message || e?.message || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      setDeleting(true);
      await api.remove(accessToken, resource._id);
      message.success("Recurso borrado");
      onDeleted?.(resource);
      onClose();
    } catch (e) {
      message.error(e?.body?.message || e?.message || "Error al borrar");
    } finally {
      setDeleting(false);
    }
  };

  const typeTag = resource.type === "video" ? "Vídeo" : "PDF";
  const typeColor = resource.type === "video" ? "geekblue" : "volcano";

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          Editar recurso
          <Tag color={typeColor} bordered={false}>
            {typeTag}
          </Tag>
        </span>
      }
      width={480}
      destroyOnClose
      extra={
        <Popconfirm
          title="Borrar recurso"
          description="Se eliminará el fichero y la entrada de BD."
          okText="Borrar"
          okButtonProps={{ danger: true, loading: deleting }}
          cancelText="Cancelar"
          onConfirm={remove}
        >
          <Button danger icon={<DeleteOutlined />} loading={deleting}>
            Borrar
          </Button>
        </Popconfirm>
      }
      footer={
        <Space style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button type="primary" onClick={submit} loading={saving}>
            Guardar cambios
          </Button>
        </Space>
      }
    >
      {error ? (
        <Alert
          type="error"
          showIcon
          message="No se pudo guardar"
          description={error}
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      ) : null}

      <Form form={form} layout="vertical" requiredMark={false}>
        <Form.Item
          name="title"
          label="Título"
          rules={[
            { required: true, message: "Pon un título" },
            { min: 3 },
            { max: 120 },
          ]}
        >
          <Input maxLength={120} />
        </Form.Item>

        <Form.Item
          name="description"
          label="Descripción"
          rules={[
            { required: true, message: "Pon una descripción" },
            { min: 3 },
            { max: 600 },
          ]}
        >
          <Input.TextArea rows={4} maxLength={600} showCount />
        </Form.Item>

        {audienceOptions.length ? (
          <Form.Item
            name="audiences"
            label={audienceLabel}
            rules={[
              { required: true, message: "Marca al menos un destinatario" },
            ]}
          >
            <Checkbox.Group options={audienceOptions} />
          </Form.Item>
        ) : null}

        {resource.type === "video" ? (
          <Form.Item
            name="url"
            label="URL del vídeo"
            rules={[
              { required: true, message: "URL requerida" },
              { type: "url", message: "URL no válida" },
            ]}
            extra="YouTube o Vimeo se previsualizan en el portal."
          >
            <Input prefix={<PlayCircleOutlined />} />
          </Form.Item>
        ) : null}

        {resource.type === "pdf" ? (
          <div style={{ marginBottom: 16 }}>
            <div
              style={{
                fontSize: 14,
                marginBottom: 6,
                color: "rgba(0,0,0,0.85)",
              }}
            >
              Fichero PDF
            </div>
            {resource.file ? (
              <div
                style={{
                  fontSize: 12,
                  color: "rgba(0,0,0,0.55)",
                  marginBottom: 8,
                  wordBreak: "break-all",
                }}
              >
                Actual: {resource.file.split("/").slice(-1)[0]}
              </div>
            ) : null}
            <Dragger
              multiple={false}
              showUploadList={false}
              beforeUpload={handlePickDocument}
              accept="application/pdf"
              style={{ padding: 8 }}
            >
              <p className="ant-upload-drag-icon" style={{ marginBottom: 4 }}>
                <InboxOutlined />
              </p>
              <p className="ant-upload-text" style={{ fontSize: 13 }}>
                {newDocument
                  ? newDocument.name
                  : "Click o arrastra para reemplazar el PDF"}
              </p>
              <p className="ant-upload-hint" style={{ fontSize: 11 }}>
                max {MAX_PDF_MB} MB · opcional
              </p>
            </Dragger>
          </div>
        ) : null}

        <div style={{ marginBottom: 8 }}>
          <div
            style={{
              fontSize: 14,
              marginBottom: 6,
              color: "rgba(0,0,0,0.85)",
            }}
          >
            {resource.type === "video" ? "Miniatura" : "Portada"}
          </div>
          {resource.image && !newImagePreview ? (
            <img
              src={resource.image}
              alt="actual"
              style={{
                maxWidth: 200,
                maxHeight: 120,
                borderRadius: 8,
                marginBottom: 8,
                display: "block",
                border: "1px solid rgba(0,0,0,0.08)",
              }}
            />
          ) : null}
          {newImagePreview ? (
            <img
              src={newImagePreview}
              alt="nueva"
              style={{
                maxWidth: 200,
                maxHeight: 120,
                borderRadius: 8,
                marginBottom: 8,
                display: "block",
                border: "1px solid rgba(0,0,0,0.08)",
              }}
            />
          ) : null}
          <Dragger
            multiple={false}
            showUploadList={false}
            beforeUpload={handlePickImage}
            accept={ALLOWED_IMAGE_TYPES.join(",")}
            style={{ padding: 8 }}
          >
            <p className="ant-upload-drag-icon" style={{ marginBottom: 4 }}>
              <PictureOutlined />
            </p>
            <p className="ant-upload-text" style={{ fontSize: 13 }}>
              Click o arrastra para reemplazar la imagen
            </p>
            <p className="ant-upload-hint" style={{ fontSize: 11 }}>
              jpg/png/webp/gif · max {MAX_IMAGE_MB} MB · opcional
            </p>
          </Dragger>
        </div>

        {resource.type === "pdf" && resource.file ? (
          <div style={{ marginTop: 12 }}>
            <Button
              icon={<FilePdfOutlined />}
              onClick={() =>
                window.open(resource.file, "_blank", "noopener,noreferrer")
              }
              block
            >
              Abrir PDF actual
            </Button>
          </div>
        ) : null}
      </Form>
    </Drawer>
  );
}
